import { Camera, CameraOptions } from '@ionic-native/camera/ngx';
import { AlertController, AlertOptions, LoadingController, LoadingOptions, ModalController } from '@ionic/angular';
import { Platform } from '@ionic/angular';
import { CropperComponent } from '../components/cropper/cropper.component';
import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { FormGroup } from '@angular/forms';
import { compressImageToBase64, compressBase64Image, IMG_COMPRESS_FAILED } from './image-compression';

@Injectable({
  providedIn: 'root',
})
export class Utils {
  constructor(
    private camera: Camera,
    private modalCtrl: ModalController,
    private platform: Platform,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
  ) {}

  async getImageFromGalleryOrFileInput(aspectRadio = 1, maintainAspectRatio = true): Promise<string> {
    try {
      let imageBase64: string;

      if (this.platform.is('cordova')) {
        // Use lower quality on Android to prevent memory issues
        // Android WebView has stricter memory limits than iOS
        const isAndroid = this.platform.is('android');
        const quality = isAndroid ? 70 : 85; // Reduced from 100 to prevent memory crashes

        console.log(`[Image Upload] Platform: ${isAndroid ? 'Android' : 'iOS'}, Quality: ${quality}`);

        const options: CameraOptions = {
          quality: quality,
          destinationType: this.camera.DestinationType.DATA_URL,
          encodingType: this.camera.EncodingType.JPEG,
          mediaType: this.camera.MediaType.PICTURE,
          sourceType: this.camera.PictureSourceType.PHOTOLIBRARY,
          correctOrientation: true,
          targetWidth: isAndroid ? 2048 : undefined, // Limit size on Android
          targetHeight: isAndroid ? 2048 : undefined,
        };

        const res = await this.camera.getPicture(options);

        // Validate camera returned data
        if (!res || res.length === 0) {
          throw new Error('Camera plugin returned empty data');
        }

        console.log(`[Image Upload] Camera returned ${res.length} characters`);

        // Camera already returns base64 with prefix, so we don't need to add it again
        const rawBase64 = res.startsWith('data:') ? res : 'data:image/jpeg;base64,' + res;

        // Validate base64 format before compression
        if (!this.validateBase64Image(rawBase64)) {
          throw new Error('Invalid base64 format from camera');
        }

        // Compress the image from camera
        imageBase64 = await compressBase64Image(rawBase64);
        console.log(`[Image Upload] After compression: ${imageBase64.length} characters`);
      } else {
        imageBase64 = await this.openFileInput();
      }

      // Clean the base64 string to prevent duplicate prefixes
      imageBase64 = this.cleanBase64String(imageBase64);

      // Validate before cropping
      if (!this.validateBase64Image(imageBase64)) {
        throw new Error('Invalid base64 format before cropping');
      }

      const cropModal = await this.modalCtrl.create({
        component: CropperComponent,
        componentProps: { imageBase64, aspectRadio, maintainAspectRatio },
      });
      await cropModal.present();
      const result = await cropModal.onDidDismiss();

      // Check if the modal returned valid data
      if (result && result.data && result.data.image) {
        // Cropper generates new uncompressed base64, so we must compress AFTER cropping
        const croppedBase64 = this.cleanBase64String(result.data.image);

        // Validate cropped image
        if (!this.validateBase64Image(croppedBase64)) {
          throw new Error('Invalid base64 format from cropper');
        }

        // Always compress the cropper output: it produces a fresh, uncompressed
        // canvas export that is frequently well over the size limit (this was the
        // root cause of large iPhone photos reaching the backend).
        imageBase64 = await compressBase64Image(croppedBase64, false);
        console.log(`[Image Upload] Final size: ${imageBase64.length} characters`);

        // Final validation
        if (!this.validateBase64Image(imageBase64)) {
          throw new Error('Invalid base64 format after final compression');
        }
      } else {
        throw new Error('No image data returned from cropper');
      }

      return imageBase64;
    } catch (error) {
      console.error('[Image Upload] Error in getImageFromGalleryOrFileInput:', error);
      console.error('[Image Upload] Platform info:', {
        isAndroid: this.platform.is('android'),
        isIOS: this.platform.is('ios'),
        isCordova: this.platform.is('cordova'),
        platforms: this.platform.platforms()
      });
      // Compression couldn't get the image under the size ceiling — tell the user
      // to pick another photo instead of letting an oversized image be uploaded.
      if (error?.message === IMG_COMPRESS_FAILED) {
        await this.showError('Não foi possível processar essa imagem. Tente outra foto.');
      }
      throw error;
    }
  }

  private cleanBase64String(base64: string): string {
    if (!base64) return '';

    // Remove any duplicate "data:image/" prefixes
    const dataUrlRegex = /^data:image\/[^;]+;base64,/;
    const matches = base64.match(dataUrlRegex);

    if (matches) {
      // If we found a prefix, remove all instances and add it back once
      const cleanBase64 = base64.replace(dataUrlRegex, '');
      return matches[0] + cleanBase64;
    }

    // If no prefix found, add the default JPEG prefix
    return 'data:image/jpeg;base64,' + base64;
  }

  private validateBase64Image(base64: string): boolean {
    if (!base64 || typeof base64 !== 'string') {
      console.error('[Image Validation] Base64 is null or not a string');
      return false;
    }

    // Check for data URL format
    const dataUrlRegex = /^data:image\/(jpeg|jpg|png);base64,/;
    if (!dataUrlRegex.test(base64)) {
      console.error('[Image Validation] Invalid data URL format');
      return false;
    }

    // Extract base64 content
    const base64Data = base64.split(',')[1];
    if (!base64Data || base64Data.length === 0) {
      console.error('[Image Validation] Empty base64 data');
      return false;
    }

    // Check if it's valid base64
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Regex.test(base64Data)) {
      console.error('[Image Validation] Invalid base64 characters');
      return false;
    }

    // Estimate size (base64 is ~4/3 of original size)
    const estimatedSizeInBytes = (base64Data.length * 3) / 4;
    const estimatedSizeInMB = estimatedSizeInBytes / (1024 * 1024);

    console.log(`[Image Validation] Estimated size: ${estimatedSizeInMB.toFixed(2)}MB`);

    // Warn if size is very large (over 10MB)
    if (estimatedSizeInMB > 10) {
      console.warn('[Image Validation] Warning: Image size exceeds 10MB, upload may fail');
    }

    return true;
  }

  private openFileInput(): Promise<string> {
    return new Promise((resolve, reject) => {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/jpeg,image/png,.jpg,.jpeg,.png';
      fileInput.onchange = async (event: any) => {
        const file = event.target.files[0];

        if (!file) {
          reject(new Error('No file selected'));
          return;
        }

        // Check if the file is a valid image
        if (!file.type.startsWith('image/')) {
          this.showError('Por favor, selecione apenas imagens (JPEG ou PNG)');
          reject(new Error('Invalid file type. Only images are allowed.'));
          return;
        }

        try {
          // Compress image before converting to base64
          const base64 = await compressImageToBase64(file);
          resolve(base64);
        } catch (error) {
          reject(error);
        }
      };
      fileInput.click();
    });
  }

  async showLoading(params?: LoadingOptions): Promise<HTMLIonLoadingElement> {
    const options: LoadingOptions = {
      message: 'Loading...',
      spinner: 'circles',
      mode: 'ios',
      ...params,
    };
    const loading = await this.loadingCtrl.create(options);

    loading.present();
    return loading;
  }

  async showConfirmationAlert(params: AlertOptions): Promise<HTMLIonAlertElement> {
    params = {
      header: 'Confirmar',
      message: 'Tem certeza que deseja continuar?',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
        },
        {
          text: 'Confirmar',
          role: 'confirm',
        },
      ],
      mode: 'ios',
      ...params,
    };
    const alert = await this.alertCtrl.create(params);
    alert.present();
    return alert;
  }

  async showError(message: string, duration = 5000): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: message,
      duration,
      cssClass: 'toast-error',
    });
    toast.present();
  }

  async showInfo(message: string, duration = 5000): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: message,
      duration,
      cssClass: 'toast-info',
    });
    toast.present();
  }

  async showSuccess(message: string, duration = 5000): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: message,
      duration,
      cssClass: 'toast-success',
    });
    toast.present();
  }

  async showWarning(message: string, duration = 5000): Promise<void> {
    const toast = await this.toastCtrl.create({
      message: message,
      duration,
      cssClass: 'toast-warning',
    });
    toast.present();
  }

  applyDocumentMask(form: FormGroup, controlName: string) {
    let document = form.get(controlName).value || '';

    if (document.length === 0) {
      // Skip processing if the document is empty
      return;
    }

    document = document.replace(/\D/g, '');

    if (document.length > 11) {
      document = document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    } else if (document.length > 8) {
      document = document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    } else if (document.length > 5) {
      document = document.replace(/(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3');
    } else if (document.length > 3) {
      document = document.replace(/(\d{3})(\d{2})$/, '$1.$2');
    }

    form.get(controlName).setValue(document);
  }

  changeDocument(form: FormGroup) {
    const documentType = form.get('documentType').value;
    let document = form.get('document').value || '';
    document = document.replace(/\D/g, '');

    if (documentType === 'cpf') {
      document = document.slice(0, 11);
      if (document.length > 3) {
        document = document.replace(/^(\d{3})(\d)/, '$1.$2');
      }
      if (document.length > 7) {
        document = document.replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3');
      }
      if (document.length > 11) {
        document = document.replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
      }
    } else if (documentType === 'cnpj') {
      document = document.slice(0, 14);
      if (document.length > 2) {
        document = document.replace(/^(\d{2})(\d)/, '$1.$2');
      }
      if (document.length > 5) {
        document = document.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
      }
      if (document.length > 8) {
        document = document.replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4');
      }
      if (document.length > 12) {
        document = document.replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
      }
    }

    form.get('document').setValue(document);
  }

  async presentAlert(message: string, icon: string, color: string) {
    const alert = await this.alertCtrl.create({
      message: message,
      cssClass: 'custom-alert',
      mode: 'ios',
      buttons: [
        {
          text: 'Fechar',
          cssClass: 'button-close',
        },
      ],
      backdropDismiss: false,
    });
    await alert.present();
    await alert.onDidDismiss();
  }

  isDocumentValid(type: string, document: string): boolean {
    type = 'cnpj';

    const digits = document
      .replace(/\D/g, '')
      .split('')
      .map((i) => Number(i));
    console.log('Digits:', digits);

    let digitsSum = 0;
    let sameNumbers = 0;

    if (type === 'cpf') {
      if (digits.length !== 11) {
        console.log('CPF length invalid');
        return false;
      }

      for (let i = 0; i < 9; i++) {
        digitsSum += digits[i] * (10 - i);
      }

      for (let i = 0; i < 11; i++) {
        if (digits[i] === digits[0]) {
          sameNumbers++;
        }
      }

      let verificatorDigit = (digitsSum * 10) % 11;
      if (digits[9] !== (verificatorDigit > 9 ? 0 : verificatorDigit) || sameNumbers > 10) {
        console.log('CPF first digit invalid');
        return false;
      }

      digitsSum = 0;
      for (let i = 0; i < 10; i++) {
        digitsSum += digits[i] * (11 - i);
      }
      verificatorDigit = (digitsSum * 10) % 11;
      const isValid = digits[10] === (verificatorDigit > 9 ? 0 : verificatorDigit);
      console.log('CPF validation result:', isValid);
      return isValid;
    }

    if (type === 'cnpj') {
      if (digits.length !== 14) {
        console.log('CNPJ length invalid');
        return false;
      }
      let multiplier = 5;
      for (let i = 0; i < digits.length; i++) {
        if (i >= 12) {
          break;
        }
        if (multiplier === 1) {
          multiplier = 9;
        }
        digitsSum += digits[i] * multiplier;
        multiplier--;
      }
      let verificatorDigit = digitsSum % 11 < 2 ? 0 : 11 - (digitsSum % 11);
      if (verificatorDigit !== digits[12]) {
        console.log('CNPJ first digit invalid');
        return false;
      }
      digitsSum = 0;
      multiplier = 6;
      for (let i = 0; i < digits.length; i++) {
        if (i >= 13) {
          break;
        }
        if (multiplier === 1) {
          multiplier = 9;
        }
        digitsSum += digits[i] * multiplier;
        multiplier--;
      }
      verificatorDigit = digitsSum % 11 < 2 ? 0 : 11 - (digitsSum % 11);
      const isValid = verificatorDigit === digits[13];
      console.log('CNPJ validation result:', isValid);
      return isValid;
    }

    return false;
  }

  isMobileResolution(): boolean {
    return this.platform.width() <= 768;
  }
}
